/**
 * Intent Hub SoT — entry modes for `/agentic` (and classic bridges).
 * Sprint B: routing + hub UI. Sprint C: honor requiredSteps in the engagement engine.
 */

import type { AgenticStepId } from '../types/agenticEngagement';
import {
  subtypeQuestionIdForProduct,
} from './intakeSubtype';
import { startSeedFromState, applyStartSeedIntake } from './engagementStartSeed.js';
import { INTENT_AI_ACTIONS, type AgenticAiAction } from './intentAiActions';

export type { AgenticAiAction };

export type AgenticJourneyMode = 'full' | 'task';

/** Outcomes the hub can start. */
export type AgenticIntentId =
  | 'full'
  | 'hld'
  | 'estimate'
  | 'migration'
  | 'systems_integration'
  | 'change_on_platform'
  | 'iot'
  | 'scale_deadline'
  | 'ai_cost'
  | 'compare'
  | 'optimize'
  | 'rfp'
  | 'review';

export type IntentPlanBadge = 'free' | 'pro';

export type AgenticIntentKind = 'agentic' | 'classic_bridge';

/** What Results should emphasize for this hub card (not “whatever pack artifacts exist”). */
export type ResultsFocus =
  | 'pack'
  | 'architecture'
  | 'estimate'
  | 'ai_cost'
  | 'plan'
  | 'compare'
  | 'optimize'
  | 'rfp'
  | 'review';

/** Sprint E — intent-specific intake (not one question bank for all tasks). */
export interface IntentIntakeProfile {
  /** Ordered discovery question ids. `product_type` auto-injects subtype when present. */
  discoveryIds: string[];
  /** Ordered requirements question ids. */
  requirementsIds: string[];
  /** Limit product_type options; omit = all catalog options. */
  productTypeFilter?: string[];
  /** Prefill when starting this intent (only fills missing keys). */
  seedAnswers?: Record<string, string>;
  /** i18n key for short intake banner */
  intakeHintKey?: string;
  // Primary-binding honesty (ADR-019): agenticIntakeProfiles → primaryBindingGate only.
}

/**
 * Hub honesty — journey vs thin wrapper.
 * `preset` = same engine as `of` with seeds; `depth_cut` = shorter pack of `of`.
 */
export type HubRelationKind = 'journey' | 'preset' | 'depth_cut';

export interface HubRelation {
  kind: HubRelationKind;
  /** Parent intent this card is a preset or depth cut of. */
  of?: AgenticIntentId;
  /** i18n key for hub badge (e.g. hub.relation.preset_optimize). */
  badgeKey?: string;
  /** i18n key for honesty footer under the card. */
  honestyKey?: string;
}

export interface AgenticIntentDef {
  id: AgenticIntentId;
  kind: AgenticIntentKind;
  /** Hub section */
  section: 'primary' | 'task';
  mode: AgenticJourneyMode;
  /** Omitted for full journey */
  intent?: Exclude<AgenticIntentId, 'full'>;
  titleKey: string;
  descriptionKey: string;
  ctaKey: string;
  /** One-line outcome on the card — what you walk away with (not process jargon). */
  outcomeKey?: string;
  /** Hub uniqueness honesty (Wave A). */
  hubRelation?: HubRelation;
  planBadge: IntentPlanBadge;
  /** When set, hub card shows a Pro teaser line (soft funnel or residual hard wall). */
  premiumTeaserKey?: string;
  /** Guest cannot start this intent (deep-link + hub) — e.g. brief/review paste. */
  requiresAuth?: boolean;
  /**
   * Hard wall at hub (rare). Prefer planBadge=pro soft funnel: Free starts;
   * Pro unlocks report panels (rfpGapAnalysis / architectureReview / board pack).
   */
  requiresPremium?: boolean;
  /** Sprint C — progress / gates / target artifact */
  requiredSteps: AgenticStepId[];
  targetArtifact: string;
  /** Sprint D — AI actions allowed for this intent (1 credit each when used) */
  aiActionsAllowed: AgenticAiAction[];
  /** Sprint E — differentiated questions per task */
  intake?: IntentIntakeProfile;
  /** Classic wizard option id when kind === classic_bridge */
  classicUseCaseId?: string;
}

export const AGENTIC_ENGAGEMENT_STORAGE_KEY = 'agenticEngagementState';
export const AGENTIC_ENGAGEMENT_ID_KEY = 'agenticEngagementId';

export const AGENTIC_INTENTS: AgenticIntentDef[] = [
  {
    id: 'full',
    kind: 'agentic',
    section: 'primary',
    mode: 'full',
    titleKey: 'hub.intents.full.title',
    descriptionKey: 'hub.intents.full.description',
    ctaKey: 'hub.intents.full.cta',
    outcomeKey: 'hub.intents.full.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: [
      'discovery',
      'requirements',
      'architecture',
      'risks',
      'estimate',
      'team',
      'plan',
      'summary',
    ],
    targetArtifact: 'engagement_pack',
    aiActionsAllowed: INTENT_AI_ACTIONS.full,
    intake: {
      // Architecture-first: pack_audience is pack-only — ask after primary drivers.
      discoveryIds: ['product_type', 'users', 'stage', 'pack_audience'],
      requirementsIds: ['scale', 'priority_nfr', 'capability_scope', 'constraints', 'integration_landscape'],
      intakeHintKey: 'hub.intake.full',
    },
  },
  {
    id: 'hld',
    kind: 'agentic',
    section: 'primary',
    mode: 'task',
    intent: 'hld',
    titleKey: 'hub.intents.hld.title',
    descriptionKey: 'hub.intents.hld.description',
    ctaKey: 'hub.intents.hld.cta',
    outcomeKey: 'hub.intents.hld.outcome',
    hubRelation: {
      kind: 'depth_cut',
      of: 'full',
      badgeKey: 'hub.relation.depth_cut_full',
      honestyKey: 'hub.relation.honesty.hld',
    },
    planBadge: 'free',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.hld,
    intake: {
      // HLD = shorter full — same decision keys, no estimate marathon / pack tone first.
      discoveryIds: ['product_type', 'users'],
      requirementsIds: ['scale', 'priority_nfr', 'capability_scope', 'constraints'],
      intakeHintKey: 'hub.intake.hld',
    },
  },
  {
    id: 'estimate',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'estimate',
    titleKey: 'hub.intents.estimate.title',
    descriptionKey: 'hub.intents.estimate.description',
    ctaKey: 'hub.intents.estimate.cta',
    outcomeKey: 'hub.intents.estimate.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    premiumTeaserKey: 'hub.intents.estimate.pro_teaser',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'estimate', 'summary'],
    targetArtifact: 'estimate',
    aiActionsAllowed: INTENT_AI_ACTIONS.estimate,
    intake: {
      // Job-shaped: decision the numbers support + product drivers — no landscape marathon.
      discoveryIds: ['estimate_focus', 'product_type', 'users', 'stage'],
      requirementsIds: ['scale', 'priority_nfr', 'constraints'],
      intakeHintKey: 'hub.intake.estimate',
    },
  },
  {
    id: 'migration',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'migration',
    titleKey: 'hub.intents.migration.title',
    descriptionKey: 'hub.intents.migration.description',
    ctaKey: 'hub.intents.migration.cta',
    outcomeKey: 'hub.intents.migration.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'plan', 'summary'],
    targetArtifact: 'plan',
    aiActionsAllowed: INTENT_AI_ACTIONS.migration,
    intake: {
      discoveryIds: ['migration_from', 'migration_approach', 'migration_style', 'users', 'pack_audience'],
      requirementsIds: ['scale', 'priority_nfr', 'capability_scope', 'constraints', 'integration_landscape'],
      seedAnswers: { product_type: 'migration', stage: 'migration' },
      intakeHintKey: 'hub.intake.migration',
    },
  },
  {
    id: 'systems_integration',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'systems_integration',
    titleKey: 'hub.intents.systems_integration.title',
    descriptionKey: 'hub.intents.systems_integration.description',
    ctaKey: 'hub.intents.systems_integration.cta',
    outcomeKey: 'hub.intents.systems_integration.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'risks', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.systems_integration,
    intake: {
      discoveryIds: ['integration_style', 'process_domain', 'process_change', 'users', 'pack_audience'],
      requirementsIds: ['scale', 'priority_nfr', 'capability_scope', 'constraints', 'locked_source', 'locked_target', 'integration_landscape'],
      seedAnswers: { product_type: 'integration', stage: 'growth' },
      intakeHintKey: 'hub.intake.systems_integration',
    },
  },
  {
    id: 'change_on_platform',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'change_on_platform',
    titleKey: 'hub.intents.change_on_platform.title',
    descriptionKey: 'hub.intents.change_on_platform.description',
    ctaKey: 'hub.intents.change_on_platform.cta',
    outcomeKey: 'hub.intents.change_on_platform.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.change_on_platform,
    intake: {
      // Delta only — pick locked estate + change type; skip greenfield marathon.
      discoveryIds: ['platform_profile_preset', 'platform_change_type', 'users'],
      requirementsIds: ['priority_nfr', 'constraints'],
      seedAnswers: { product_type: 'integration', stage: 'growth' },
      intakeHintKey: 'hub.intake.change_on_platform',
    },
  },
  {
    id: 'iot',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'iot',
    titleKey: 'hub.intents.iot.title',
    descriptionKey: 'hub.intents.iot.description',
    ctaKey: 'hub.intents.iot.cta',
    outcomeKey: 'hub.intents.iot.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.iot,
    intake: {
      // Job-shaped: style + placement decide topology; drop shared landscape marathon.
      discoveryIds: ['iot_style', 'iot_placement', 'users'],
      requirementsIds: ['scale', 'priority_nfr', 'constraints'],
      seedAnswers: { product_type: 'iot', stage: 'growth' },
      intakeHintKey: 'hub.intake.iot',
    },
  },
  {
    id: 'scale_deadline',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'scale_deadline',
    titleKey: 'hub.intents.scale_deadline.title',
    descriptionKey: 'hub.intents.scale_deadline.description',
    ctaKey: 'hub.intents.scale_deadline.cta',
    outcomeKey: 'hub.intents.scale_deadline.outcome',
    hubRelation: {
      kind: 'preset',
      of: 'optimize',
      badgeKey: 'hub.relation.preset_optimize',
      honestyKey: 'hub.relation.honesty.scale_deadline',
    },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'risks', 'estimate', 'summary'],
    targetArtifact: 'optimize',
    aiActionsAllowed: INTENT_AI_ACTIONS.scale_deadline,
    intake: {
      // Optimize preset — peak shape first; performance / scalability seeded.
      discoveryIds: ['peak_load_shape', 'product_type', 'users'],
      requirementsIds: ['scale', 'constraints'],
      seedAnswers: {
        optimize_focus: 'performance',
        priority_nfr: 'scalability',
        stage: 'growth',
      },
      intakeHintKey: 'hub.intake.scale_deadline',
    },
  },
  {
    id: 'ai_cost',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'ai_cost',
    titleKey: 'hub.intents.ai_cost.title',
    descriptionKey: 'hub.intents.ai_cost.description',
    ctaKey: 'hub.intents.ai_cost.cta',
    outcomeKey: 'hub.intents.ai_cost.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    premiumTeaserKey: 'hub.intents.ai_cost.pro_teaser',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'estimate', 'summary'],
    targetArtifact: 'estimate',
    aiActionsAllowed: INTENT_AI_ACTIONS.ai_cost,
    intake: {
      // Path already implies AI — skip generic “what are you building”
      discoveryIds: ['ai_workload', 'ai_volume', 'ai_serving', 'users'],
      // constraints required so GDPR / regulated / on-prem can boost Secure Enterprise related
      requirementsIds: ['priority_nfr', 'constraints'],
      seedAnswers: { product_type: 'ai', stage: 'mvp_build' },
      intakeHintKey: 'hub.intake.ai_cost',
    },
  },
  {
    id: 'compare',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'compare',
    titleKey: 'hub.intents.compare.title',
    descriptionKey: 'hub.intents.compare.description',
    ctaKey: 'hub.intents.compare.cta',
    outcomeKey: 'hub.intents.compare.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'summary'],
    targetArtifact: 'compare',
    aiActionsAllowed: INTENT_AI_ACTIONS.compare,
    intake: {
      // users required for CAB HARD gate (users.primary) before dual architecture.
      discoveryIds: ['product_type', 'users', 'compare_axis'],
      requirementsIds: ['scale', 'priority_nfr'],
      intakeHintKey: 'hub.intake.compare',
    },
  },
  {
    id: 'optimize',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'optimize',
    titleKey: 'hub.intents.optimize.title',
    descriptionKey: 'hub.intents.optimize.description',
    ctaKey: 'hub.intents.optimize.cta',
    outcomeKey: 'hub.intents.optimize.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiredSteps: ['discovery', 'requirements', 'architecture', 'risks', 'estimate', 'summary'],
    targetArtifact: 'optimize',
    aiActionsAllowed: INTENT_AI_ACTIONS.optimize,
    intake: {
      // Brownfield job-shaped: pressure + baseline + system — no landscape marathon.
      discoveryIds: ['optimize_focus', 'optimize_baseline', 'product_type', 'users'],
      requirementsIds: ['scale', 'priority_nfr', 'constraints'],
      intakeHintKey: 'hub.intake.optimize',
    },
  },
  {
    id: 'rfp',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'rfp',
    titleKey: 'hub.intents.rfp.title',
    descriptionKey: 'hub.intents.rfp.description',
    ctaKey: 'hub.intents.rfp.cta',
    outcomeKey: 'hub.intents.rfp.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiresAuth: true,
    premiumTeaserKey: 'hub.intents.rfp.pro_teaser',
    // Brief → architecture (no requirements marathon; infer fills product/users).
    // Intake 1.7.0: confirm product/users when inference used defaults or completeness is LOW.
    // Soft Pro: Free can paste + get architecture; gap analysis / board pack = Pro.
    requiredSteps: ['discovery', 'architecture', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.rfp,
    intake: {
      // Paste only — heuristics seed product/users (defaults when weak). No clarifying cards.
      discoveryIds: ['rfp_brief'],
      requirementsIds: [],
      seedAnswers: { stage: 'mvp_build' },
      intakeHintKey: 'hub.intake.rfp',
    },
  },
  {
    id: 'review',
    kind: 'agentic',
    section: 'task',
    mode: 'task',
    intent: 'review',
    titleKey: 'hub.intents.review.title',
    descriptionKey: 'hub.intents.review.description',
    ctaKey: 'hub.intents.review.cta',
    outcomeKey: 'hub.intents.review.outcome',
    hubRelation: { kind: 'journey' },
    planBadge: 'pro',
    requiresAuth: true,
    premiumTeaserKey: 'hub.intents.review.pro_teaser',
    // Soft Pro: Free can paste HLD + get nearest catalog match; theme-gap panel = Pro.
    requiredSteps: ['discovery', 'architecture', 'summary'],
    targetArtifact: 'architecture',
    aiActionsAllowed: INTENT_AI_ACTIONS.review,
    intake: {
      discoveryIds: ['hld_paste'],
      requirementsIds: [],
      intakeHintKey: 'hub.intake.review',
    },
  },
];

export function getIntentDef(id: AgenticIntentId): AgenticIntentDef | undefined {
  return AGENTIC_INTENTS.find((i) => i.id === id);
}

/** Guest cannot open intents marked requiresAuth (e.g. RFP). */
export function isIntentBlockedForGuest(def: AgenticIntentDef | undefined | null): boolean {
  return Boolean(def?.requiresAuth);
}

export {
  hubIntentPlanBadgeKey,
  hubIntentBadgeTone,
  hubIntentCtaKey,
  hubIntentFooterHintKey,
} from './hubIntentAuthCopy';

/** Free cannot open intents marked requiresPremium (hard wall — prefer soft planBadge=pro). */
export function isIntentBlockedForPremium(
  def: AgenticIntentDef | undefined | null,
  access: { canViewPremiumArtifacts?: boolean; tier?: string } = {},
): boolean {
  if (!def?.requiresPremium) return false;
  return !access.canViewPremiumArtifacts;
}

export function primaryIntents(): AgenticIntentDef[] {
  return AGENTIC_INTENTS.filter((i) => i.section === 'primary');
}

export function taskIntents(): AgenticIntentDef[] {
  return AGENTIC_INTENTS.filter((i) => i.section === 'task');
}

/** Build `/agentic?mode=…&intent=…` (intent omitted for full). */
export function buildAgenticPath(def: AgenticIntentDef): string {
  const params = new URLSearchParams();
  params.set('mode', def.mode);
  if (def.mode === 'task' && def.intent) {
    params.set('intent', def.intent);
  }
  return `/agentic?${params.toString()}`;
}

export function parseHubSearchParams(search: string): {
  mode: AgenticJourneyMode;
  intent: AgenticIntentId | null;
} {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const modeRaw = params.get('mode');
  const intentRaw = params.get('intent');
  const mode: AgenticJourneyMode = modeRaw === 'task' ? 'task' : 'full';
  const known = AGENTIC_INTENTS.map((i) => i.id);
  const intent =
    intentRaw && (known as string[]).includes(intentRaw)
      ? (intentRaw as AgenticIntentId)
      : null;
  return { mode, intent };
}

/** Optional L1 seed from pattern catalog (#19) — architecture step prefers this pattern. */
export function parsePatternFromSearch(search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('pattern');
  return raw && /^PATTERN_[A-Z0-9_]+$/.test(raw) ? raw : null;
}

/** Deep-link from `/dashboard/patterns` → full journey with preferred L1. */
export function buildPatternStartPath(patternId: string): string {
  const params = new URLSearchParams();
  params.set('mode', 'full');
  if (/^PATTERN_[A-Z0-9_]+$/.test(patternId)) {
    params.set('pattern', patternId);
  }
  return `/agentic?${params.toString()}`;
}

export interface AgenticDraftHint {
  currentStepId: string;
  answerCount: number;
  hasArchitecture: boolean;
}

/** True when session has a meaningful in-progress engagement (Continue CTA). */
export function readAgenticDraftHint(): AgenticDraftHint | null {
  try {
    const raw = sessionStorage.getItem(AGENTIC_ENGAGEMENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      currentStepId?: string;
      answers?: Record<string, string>;
      completedSteps?: string[];
      artifacts?: { architecture?: unknown };
    };
    const answerCount = Object.keys(parsed.answers || {}).length;
    const completed = parsed.completedSteps?.length || 0;
    if (answerCount === 0 && completed === 0) return null;
    return {
      currentStepId: parsed.currentStepId || 'discovery',
      answerCount,
      hasArchitecture: Boolean(parsed.artifacts?.architecture),
    };
  } catch {
    return null;
  }
}

export function clearAgenticSessionDraft(): void {
  try {
    sessionStorage.removeItem(AGENTIC_ENGAGEMENT_STORAGE_KEY);
    sessionStorage.removeItem(AGENTIC_ENGAGEMENT_ID_KEY);
    sessionStorage.removeItem('agenticWizardState');
  } catch {
    /* ignore */
  }
}

/** Resolve intent def from engagement hub fields (default = full). */
export function resolveIntentDefFromState(state: {
  mode?: AgenticJourneyMode | null;
  intent?: string | null;
}): AgenticIntentDef {
  if (state.mode === 'task' && state.intent) {
    const byIntent = AGENTIC_INTENTS.find(
      (i) => i.kind === 'agentic' && i.intent === state.intent
    );
    if (byIntent) return byIntent;
  }
  return getIntentDef('full')!;
}

export function isFullJourney(state: {
  mode?: AgenticJourneyMode | null;
}): boolean {
  return !state.mode || state.mode === 'full';
}

/**
 * Wizard progress chips (discovery → … → summary).
 * Pack-only steps (estimate/plan) stay inside Results — not separate chips.
 */
export function wizardProgressStepsForIntent(def: AgenticIntentDef): AgenticStepId[] {
  const WIZARD: AgenticStepId[] = [
    'discovery',
    'requirements',
    'architecture',
    'summary',
  ];
  if (def.mode === 'full' || def.id === 'full') return [...WIZARD];
  return WIZARD.filter(
    (s) => def.requiredSteps.includes(s) || s === 'summary'
  );
}

/**
 * Intent-aware minimum discovery before estimate/pack.
 * Uses the hub intake profile only — do not hard-require fields the intake never asked
 * (e.g. HLD skips `stage`; forcing it blocked “See recommendation” after a valid HLD path).
 * Subtype cards required when product_type (or seeded product) implies a follow-up.
 */
export function discoveryLiteComplete(
  answers: Record<string, string>,
  hub: {
    mode?: AgenticJourneyMode | null;
    intent?: string | null;
    patternLockSource?: 'catalog' | 'related_switch' | null;
    preferredPatternId?: string | null;
  } = {}
): boolean {
  const def = resolveIntentDefFromState(hub);
  const seed = startSeedFromState(hub);
  const baseIntake = def.intake || {
    discoveryIds: ['product_type', 'users', 'stage'],
    requirementsIds: ['scale', 'priority_nfr', 'constraints'],
  };
  const intake = applyStartSeedIntake(baseIntake, seed);
  const merged = { ...(intake.seedAnswers || {}), ...answers };
  const ids = intake.discoveryIds.length
    ? intake.discoveryIds
    : ['product_type', 'users', 'stage'];

  for (const id of ids) {
    if (!merged[id]) return false;
  }

  if (def.id === 'rfp') {
    // Brief body is in notes.rfp_brief; discovery answer is the sentinel `pasted`.
    return Boolean(merged.rfp_brief);
  }

  if (def.id === 'review') {
    return Boolean(merged.hld_paste);
  }

  const productForSubtype = ids.includes('product_type') ? merged.product_type : null;
  const subtypeId = subtypeQuestionIdForProduct(productForSubtype || undefined);
  if (
    subtypeId
    && !ids.includes(subtypeId)
    && ids.includes('product_type')
    && !merged[subtypeId]
  ) {
    return false;
  }

  return true;
}

/** Whether the task's primary artifact exists. */
export function taskTargetMet(
  def: AgenticIntentDef,
  artifacts: {
    architecture?: unknown;
    architectureAlt?: unknown;
    compare?: unknown;
    estimate?: unknown;
    plan?: unknown;
    risks?: unknown;
  }
): boolean {
  switch (def.targetArtifact) {
    case 'architecture':
      return Boolean(artifacts.architecture);
    case 'estimate':
      return Boolean(artifacts.estimate);
    case 'plan':
      return Boolean(artifacts.plan);
    case 'compare':
      return Boolean(artifacts.architecture && artifacts.architectureAlt && artifacts.compare);
    case 'optimize':
      return Boolean(artifacts.architecture && artifacts.risks && artifacts.estimate);
    case 'engagement_pack':
      return Boolean(
        artifacts.architecture
        && artifacts.risks
        && artifacts.estimate
        && artifacts.plan
      );
    default:
      return Boolean(artifacts.architecture);
  }
}

/** Sprint D — credit cost per AI action (matches backend increment = 1). */
export const AI_ACTION_CREDIT_COST: Record<AgenticAiAction, number> = {
  agentic_chat: 1,
  architecture_desk: 1,
  generate_agnostic_blueprint: 1,
  generate_engagement_pack: 1,
  generate_technologist_advisory: 1,
  generate_gap_close_proposal: 1,
};

export function isAiActionAllowedForIntent(
  def: AgenticIntentDef,
  action: AgenticAiAction
): boolean {
  return (def.aiActionsAllowed || []).includes(action);
}

export function creditCostForAction(action: AgenticAiAction): number {
  return AI_ACTION_CREDIT_COST[action] ?? 1;
}

/**
 * Results UX focus from hub intent — keeps Quick estimate / HLD / AI cost
 * from looking identical to the full board pack when fallback pack fills all slots.
 */
export function resultsFocusForIntent(def: AgenticIntentDef): ResultsFocus {
  if (def.mode === 'full' || def.id === 'full') return 'pack';
  if (def.id === 'ai_cost') return 'ai_cost';
  if (def.id === 'optimize' || def.id === 'scale_deadline') return 'optimize';
  if (def.id === 'rfp') return 'rfp';
  if (def.id === 'review') return 'review';
  switch (def.targetArtifact) {
    case 'architecture':
      return 'architecture';
    case 'estimate':
      return 'estimate';
    case 'plan':
      return 'plan';
    case 'compare':
      return 'compare';
    case 'optimize':
      return 'optimize';
    case 'engagement_pack':
      return 'pack';
    default:
      return 'pack';
  }
}

